import React, { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  ThemeProvider,
  Typography,
  createTheme,
  useTheme,
} from '@mui/material';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';

import { ComparisonPlot, SharePlot, seriesColour } from './FidelityPlots';
import {
  type Point,
  RESET_PATCH,
  type State,
  VIEWS,
  compact,
  fmt,
  isCR,
  metricLabel,
  metricOptions,
  metricPatch,
  pct,
  periods,
  selected,
  sourceURL,
} from './fidelityModel';
import { type Dashboard, exportCountries } from './liveDataAdapter';

export default function CountriesContent({
  state,
  dashboard: d,
  onChange,
  error,
  loading,
  onRetry,
}: {
  state: State;
  dashboard: Dashboard;
  onChange: (patch: Partial<State>) => void;
  error?: string;
  loading: boolean;
  onRetry: () => void;
}) {
  const hostTheme = useTheme();
  const dark = hostTheme.palette.mode === 'dark';
  const theme = useMemo(
    () =>
      createTheme(hostTheme, {
        palette: { primary: { main: dark ? '#4EFEB9' : '#0F464F', contrastText: dark ? '#0F0C08' : '#FFFFFF' } },
      }),
    [hostTheme, dark],
  );
  const [advanced, setAdvanced] = useState(false);
  const [method, setMethod] = useState(false);
  const [csvReady, setCsvReady] = useState(false);
  const label = metricLabel(state);
  const category =
    state.view === 'Countries Margin' ? 'Country' : state.view === 'Product Margin' ? 'Product' : 'Destination';
  const source = d.source.split('.').pop();
  const pairSx = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,330px),1fr))', gap: 1.5 };
  useEffect(() => setCsvReady(false), [state, d.ready]);
  function update(p: Partial<State>) {
    onChange(p);
    setCsvReady(false);
  }
  function reset() {
    update(RESET_PATCH);
  }
  function changeView(view: State['view']) {
    update({
      view,
      highlight: 'All',
      ...(isCR(view) !== d.conversion
        ? { market: 'All', product: 'All', language: 'All', cancelled: 'All', buyer: 'All', countryGroup: 'All' }
        : {}),
    });
  }
  function single(title: string, value: string, options: Array<string>, change: (v: string) => void) {
    return (
      <FormControl size="small" sx={{ minWidth: 120, flex: '1 1 120px', maxWidth: 210 }}>
        <InputLabel>{title}</InputLabel>
        <Select
          inputProps={{ 'aria-label': title }}
          label={title}
          value={value}
          onChange={(e) => change(e.target.value)}
          sx={{ fontSize: 12 }}
        >
          {options.map((v) => (
            <MenuItem key={v} value={v} sx={{ fontSize: 12 }}>
              {v}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }
  function multi(title: string, value: string, options: Array<string>, change: (v: string) => void) {
    return (
      <FormControl size="small" sx={{ minWidth: 130, flex: '1 1 130px', maxWidth: 230 }}>
        <InputLabel>{title}</InputLabel>
        <Select
          multiple
          inputProps={{ 'aria-label': title }}
          label={title}
          value={selected(value, options)}
          renderValue={(v) => (value === 'All' ? '(All)' : v.length === 1 ? v[0] : v.length + ' selected')}
          onChange={(e) => {
            const next = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
            change(
              next.includes('All')
                ? value === 'All'
                  ? 'None'
                  : 'All'
                : next.length === options.length
                  ? 'All'
                  : next.length
                    ? next.join('|')
                    : 'None',
            );
          }}
          MenuProps={{ PaperProps: { sx: { maxHeight: 360, maxWidth: 360 } } }}
          sx={{ fontSize: 12 }}
        >
          <MenuItem value="All">
            <Checkbox size="small" checked={value === 'All'} />
            <ListItemText primary="(All)" />
          </MenuItem>
          {options.map((v) => (
            <MenuItem key={v} value={v}>
              <Checkbox size="small" checked={selected(value, options).includes(v)} />
              <ListItemText primary={v} primaryTypographyProps={{ fontSize: 12 }} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }
  function plot(title: string, points: Array<Point>, id: string, yoy = false, conversion = false, height = 240) {
    const latest = [...points].reverse().find((p) => (yoy ? p.yoy : p.current) !== null);
    return (
      <Box
        component="section"
        aria-label={title}
        sx={{ minWidth: 0, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          gap={1}
          sx={{ py: 0.75, px: 1.25, bgcolor: '#0F464F', color: '#FFFFFF', borderTop: '2px solid #4EFEB9' }}
        >
          <Typography sx={{ fontSize: 12, lineHeight: 1.4 }}>{title}</Typography>
          <Typography sx={{ fontSize: 10, flexShrink: 0 }}>
            {yoy
              ? 'Growth (%)'
              : conversion
                ? 'CR (%)'
                : ['orders', 'customers'].includes(state.metric)
                  ? 'Count'
                  : 'A$'}
          </Typography>
        </Stack>
        <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ px: 1.25, pt: 0.75 }}>
          <Typography sx={{ fontSize: 10, borderBottom: '2px solid ' + (dark ? '#4EFEB9' : '#0F464F') }}>
            {yoy ? 'Above zero' : conversion ? 'Packages / views' : 'Current'}
          </Typography>
          {!conversion && (
            <Typography sx={{ fontSize: 10, borderBottom: '2px dashed ' + (dark ? '#97BAB5' : '#7C9C99') }}>
              {yoy ? 'Below zero' : 'Prior year'}
            </Typography>
          )}
          <Typography sx={{ fontSize: 10, ml: 'auto' }}>
            Latest{' '}
            {latest
              ? yoy
                ? pct(latest.yoy)
                : conversion
                  ? pct(latest.current, 2)
                  : compact(latest.current)
              : 'Unknown'}
          </Typography>
        </Stack>
        <Box sx={{ height, minWidth: 0, pt: 0.25, px: 0.5 }}>
          <ComparisonPlot end={d.end} points={points} yoy={yoy} conversion={conversion} dark={dark} chartId={id} />
        </Box>
        <Typography sx={{ fontSize: 9, px: 1.25, py: 0.5 }} color="text.secondary">
          {source} · {d.trendStart} to {d.end}
        </Typography>
      </Box>
    );
  }
  function share() {
    return (
      <Box
        component="section"
        sx={{ minWidth: 0, bgcolor: 'background.paper', mt: state.view === 'Product Margin' ? 0 : 1.25 }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ py: 0.75, px: 1.25, bgcolor: '#0F464F', color: '#FFFFFF', borderTop: '2px solid #4EFEB9' }}
        >
          <Typography sx={{ fontSize: 12 }}>
            % Share of {label} ({state.dma}DMA)
          </Typography>
          <Typography sx={{ fontSize: 10 }}>Share (%)</Typography>
        </Stack>
        <Box sx={{ height: state.view === 'Product Margin' ? 402 : 240, minWidth: 0 }}>
          <SharePlot
            logarithmic={state.view !== 'Product Margin'}
            data={d.share}
            names={d.shareNames}
            dark={dark}
            highlight={state.highlight}
          />
        </Box>
        <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ px: 1, maxHeight: 105, overflow: 'auto' }}>
          {d.shareNames.map((n, i) => (
            <Button
              key={n}
              size="small"
              onClick={() => update({ highlight: state.highlight === n ? 'All' : n })}
              aria-pressed={state.highlight === n}
              sx={{
                fontSize: 10,
                color: 'text.primary',
                p: 0.25,
                textTransform: 'none',
                minWidth: 0,
                borderBottom: '2px solid ' + seriesColour(i, dark),
                fontWeight: state.highlight === n ? 700 : 400,
              }}
            >
              {n}
            </Button>
          ))}
          {state.highlight !== 'All' && (
            <Button size="small" onClick={() => update({ highlight: 'All' })}>
              Clear highlight
            </Button>
          )}
        </Stack>
        <Typography sx={{ fontSize: 9, px: 1.25, py: 1 }} color="text.secondary">
          {source} · {d.trendStart} to {d.end}.{' '}
          {state.view === 'Product Margin'
            ? 'Linear share scale retains non-positive values.'
            : 'Website country groups; logarithmic share scale. Non-positive values remain available in data.'}
        </Typography>
      </Box>
    );
  }
  function download() {
    if (!d.ready) {
      return;
    }
    const url = URL.createObjectURL(new Blob([exportCountries(state, d)], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `countries-${state.view.replace(/ /g, '-')}-${d.end}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setCsvReady(true);
  }
  const active = (
    d.conversion
      ? ['market', 'product', 'language']
      : [
          'market',
          'product',
          'countryGroup',
          'cancelled',
          'buyer',
          'language',
          'destination',
          'countryPanel',
          'productGroup',
          'destinationGroup',
        ]
  ).filter((k) => state[k] !== 'All');
  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="xl" sx={{ py: 1.5, minWidth: 0 }}>
        <Tabs
          value={state.view}
          variant="scrollable"
          scrollButtons="auto"
          onChange={(_, v) => changeView(v)}
          sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, fontSize: 12, textTransform: 'none', px: 1.25 } }}
        >
          {VIEWS.map((v) => (
            <Tab key={v} value={v} label={v} />
          ))}
        </Tabs>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={1} sx={{ py: 1.5 }}>
          <Box>
            <Typography variant="h5" sx={{ color: dark ? '#A9DAD0' : '#0F464F', fontSize: 23 }}>
              {d.conversion ? state.view : category === 'Country' ? 'Countries Performance' : category + ' Performance'}
            </Typography>
            <Typography sx={{ fontSize: 11 }} color="text.secondary">
              {d.conversion
                ? 'Conversion Rate = packages / total product views.'
                : 'By TTV, Margin, AoV, Orders and Unique Customers. Select a metric to update the graphs.'}
            </Typography>
          </Box>
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            <PageChatButton />
            <Button size="small" onClick={() => setMethod(true)}>
              Method and sources
            </Button>
            <Button size="small" variant="outlined" onClick={download} disabled={!d.ready}>
              {csvReady ? 'CSV ready' : 'Download CSV'}
            </Button>
          </Stack>
        </Stack>
        <Box
          role="status"
          sx={{ py: 0.5, px: 1, fontSize: 10, borderLeft: '3px solid #0F464F', bgcolor: 'action.hover' }}
        >
          {loading ? 'Loading live data' : error ? 'Live data unavailable' : d.ready ? 'Live source query' : d.problem}{' '}
          · Requested through {d.end}
          {d.queriedAt ? ' · ' + d.queriedAt : ''}
        </Box>
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" sx={{ py: 1.5 }}>
          {!d.conversion && (
            <>
              {single(
                'Period',
                state.period,
                [...periods, ...(state.period === 'Custom' ? ['Custom'] : [])],
                (period) => update({ period }),
              )}
              {single('Days Moving Average', String(state.dma), [7, 14, 21, 28, 35, 42, 49].map(String), (dma) =>
                update({ dma: +dma }),
              )}
            </>
          )}
          {multi(
            d.conversion
              ? state.view === 'Countries CR'
                ? 'Website Region Group (main chart)'
                : 'Website Region Group'
              : 'Country Name',
            state.market,
            d.options.markets,
            (market) => update({ market }),
          )}
          {d.conversion
            ? multi(
                state.view === 'Products CR' ? 'Product Name (main chart)' : 'Product Name',
                state.product,
                d.options.products,
                (product) => update({ product }),
              )
            : single('Metric', label, [...metricOptions, ...(metricOptions.includes(label) ? [] : [label])], (v) =>
                update(metricPatch(v)),
              )}
          <Button size="small" aria-expanded={advanced} onClick={() => setAdvanced(!advanced)}>
            More filters
          </Button>
          <Button size="small" onClick={reset}>
            Reset filters
          </Button>
        </Stack>
        <Collapse in={advanced}>
          <Stack direction="row" gap={1} flexWrap="wrap" sx={{ py: 1.5 }}>
            {!d.conversion && (
              <>
                {multi('Is cancelled', state.cancelled, ['Yes', 'No'], (cancelled) => update({ cancelled }))}
                {multi('Country Group', state.countryGroup, d.options.groups, (countryGroup) =>
                  update({ countryGroup }),
                )}
                {multi('Buyer Type', state.buyer, ['New', 'Repeat'], (buyer) => update({ buyer }))}
                {multi('Product Name', state.product, d.options.products, (product) => update({ product }))}
              </>
            )}
            {multi('Website Language', state.language, d.options.languages, (language) => update({ language }))}
          </Stack>
        </Collapse>
        {active.length > 0 && (
          <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ pb: 1 }}>
            {active.map((k) => (
              <Chip
                size="small"
                key={k}
                label={`${k}: ${selected(state[k], []).length === 1 ? state[k] : selected(state[k], []).length + ' selected'}`}
                onDelete={() => update({ [k]: 'All' })}
              />
            ))}
          </Stack>
        )}
        {!d.valid && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {d.problem}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error} <Button onClick={onRetry}>Retry</Button>
          </Alert>
        )}
        {!loading && !error && d.valid && !d.ready && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {d.problem}
          </Alert>
        )}
        {d.conversion ? (
          <>
            {plot(
              'CR Overall – ' + (state.view === 'Countries CR' ? 'Country' : 'Product') + ' Dash',
              d.overview,
              'native-cr-overall',
              false,
              true,
              325,
            )}
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              {state.view === 'Countries CR' ? 'CR by Website Region' : 'CR by Product'}
            </Typography>
            <Typography sx={{ fontSize: 11 }} color="text.secondary">
              {state.view === 'Countries CR'
                ? 'Website Region Group filters the main chart only; product and language filter all country rows.'
                : 'Product Name filters the main chart only; region and language filter all product rows.'}
            </Typography>
            {d.panels.map((p, i) => (
              <Box
                key={p.name}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '145px minmax(0,1fr)' },
                  borderBottom: 1,
                  borderColor: 'divider',
                  mt: 1,
                }}
              >
                <Typography sx={{ alignSelf: 'center', px: 1.5, py: 1, fontSize: 12, color: 'primary.main' }}>
                  {p.name}
                </Typography>
                {plot(p.name + ' CR', p.points, 'native-cr-' + i, false, true, 210)}
              </Box>
            ))}
          </>
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '175px minmax(0,1fr)' },
                gap: 1.5,
                alignItems: 'start',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2,minmax(0,1fr))', sm: 'repeat(3,minmax(0,1fr))', md: '1fr' },
                  gap: { xs: 0.5, md: 0 },
                }}
              >
                {d.kpis.map((k) => (
                  <Button
                    key={k.label}
                    data-lens-pdf-keep
                    aria-pressed={state.metric === k.metric && state.basis === k.basis}
                    onClick={() => update({ metric: k.metric, basis: k.basis })}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderLeft:
                        '3px solid ' +
                        (state.metric === k.metric && state.basis === k.basis ? '#0F464F' : 'transparent'),
                      bgcolor:
                        state.metric === k.metric && state.basis === k.basis ? 'action.selected' : 'background.paper',
                      color: 'text.primary',
                      p: 1,
                      minHeight: 72,
                      display: 'flex',
                      flexDirection: 'column',
                      textTransform: 'none',
                      borderRadius: 0,
                    }}
                  >
                    <Typography sx={{ fontSize: 10, color: 'primary.main' }}>
                      {state.period} · {k.label}
                    </Typography>
                    <Typography sx={{ fontSize: 21, lineHeight: 1.2 }}>
                      {k.current === null
                        ? 'Unknown'
                        : ['orders', 'customers'].includes(k.metric)
                          ? fmt(k.current)
                          : 'A$' + fmt(k.current)}
                    </Typography>
                    <Typography sx={{ fontSize: 10 }} color="text.secondary">
                      YoY growth {pct(k.growth)} {k.growth === null ? '' : k.growth >= 0 ? '↑' : '↓'}
                    </Typography>
                  </Button>
                ))}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                {state.view !== 'Product Margin' && (
                  <Box sx={pairSx}>
                    {plot(label + ` (${state.dma}DMA)`, d.overview, 'native-overall-value')}
                    {plot(label + ` YoY Growth (${state.dma}DMA)`, d.overview, 'native-overall-yoy', true)}
                  </Box>
                )}
                {share()}
              </Box>
            </Box>
            <Box sx={{ mt: 2, mb: 1, py: 1, px: 1.5, bgcolor: 'action.hover', borderLeft: '3px solid #0F464F' }}>
              <Typography variant="h6" sx={{ color: 'primary.main', fontSize: 17 }}>
                {label} by {category} ({state.dma}DMA)
              </Typography>
              <Typography sx={{ fontSize: 10 }} color="text.secondary">
                Daily trend compared with the source-aligned prior year. Original fixed panel order.
              </Typography>
            </Box>
            {d.panels.map((p, i) => (
              <Box key={p.name} sx={{ ...pairSx, mt: 1.5 }}>
                {plot(label + ' – ' + p.name, p.points, `native-segment-${i}-value`, false, false, 215)}
                {plot(label + ' YoY Growth – ' + p.name, p.points, `native-segment-${i}-yoy`, true, false, 215)}
              </Box>
            ))}
          </>
        )}
        <Typography sx={{ fontSize: 10, mt: 2 }} color="text.secondary">
          Live authenticated Lens data. KTR excluded.{' '}
          <Button size="small" href={sourceURL(state.view)} target="_blank" rel="noreferrer">
            Open current original
          </Button>
        </Typography>
        <Dialog open={method} onClose={() => setMethod(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Method and sources</DialogTitle>
          <DialogContent dividers>
            <Typography sx={{ mb: 2 }}>
              Source: {d.source}. {d.queriedAt ? d.queriedAt + '.' : ''}
            </Typography>
            {d.conversion ? (
              <Typography sx={{ mb: 2 }}>
                Conversion rate (CR) is packages sold ÷ product views for each Sydney day, shown for the last 90 days
                without smoothing. On Countries CR, Website Region filters the main chart only; on Products CR, Product
                Name filters the main chart only. Website language filters every chart.
              </Typography>
            ) : (
              <>
                <Typography sx={{ mb: 2 }}>
                  Summary tiles cover the selected period ({d.start} to {d.summaryEnd}, Sydney dates). Charts show the
                  last 90 days as a {state.dma}-day moving average; days without data are left out of the average.
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  BR is before refunds; AR is after refunds. Last year (LY) uses the source&apos;s own prior-year values
                  for the same dates, and YoY growth is current ÷ LY − 1. Orders, unique customers and AoV (TTV ÷
                  orders) are counted across the whole selection, not added up from the panels.
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  % share is each group&apos;s moving-average metric ÷ the overall moving-average metric. Country share
                  uses a logarithmic scale; product share uses a linear scale.
                </Typography>
              </>
            )}
            <Typography>
              Filters apply to the current and LY figures alike. Clearing every value of a filter shows no data for it.
              Highlighting a legend entry does not change the population.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMethod(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </ThemeProvider>
  );
}
